/** /zh/ai-prd — Chinese version of the AI PRD product landing page.
 *  Native zh content, same structure as AiPrd.tsx. */
import { MarketingShell } from './MarketingShell';
import { TemplateCta } from './TemplateCta';

export function AiPrdZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/ai-prd"
      documentTitle="AI PRD 工具：先写出来，再别让它漂移 · Draftlize"
      documentDescription="一堆工具都能用 AI 一键生成 PRD。但生成只是第一天——PRD 写出来后，产品往前走，文档就开始悄悄漂移。Draftlize 让 PRD 里的每个决策成为一张卡片：改一处，所有依赖它的卡片自动标记 stale，AI agent 始终读到最新版。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">产品 · AI PRD</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            AI 能写出 PRD。<br/>
            <em>难的是让它一直成立。</em>
          </h1>
          <p className="lede">
            用 AI 生成一份 <a href="/zh/product-requirements-document" style={{ color: 'var(--amber)' }}>PRD</a>，早就不是问题——十几个工具都能从一句 prompt 几秒钟出一份。但 PRD 不是写出来那天就算完了，那天恰恰是它开始漂移的起点。产品往前走，一个决策变了，周一读着还天衣无缝的文档，到周五就悄悄错了——而且没人知道是哪一段过的时。Draftlize 接手的，正是那些生成工具不管的部分：它把 PRD 里的每个决策变成一张可寻址的卡片，一处一改，所有依赖它的卡片自动把自己标成 stale——agent 读到的，永远是那个还成立的版本。
          </p>
          <div style={{ marginTop: 32, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <a className="btn-primary" href="/">用 $5 免费开始</a>
            <span style={{ fontSize: 14, color: 'var(--ink-2)' }}>无需绑卡，按量付费。</span>
          </div>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">TL;DR</span>
            <h2 className="serif">生成一份 PRD vs <em>让它一直活着。</em></h2>
          </div>
          <p>AI PRD 生成器递给你一份漂亮文档，然后转身就走。从那一刻起它就是一张快照——每一次决策变更都让它错一点，却没有东西告诉你错在哪。Draftlize 盯的，是初稿之后那条长长的尾巴，真正的成本都在那里。</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>一个典型的 AI PRD 生成器</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>从 prompt 出初稿</td><td className="ours yes">能——直接落成结构化卡片</td><td className="partial">能——落成一份大文档</td></tr>
            <tr><td>你拿回来的东西</td><td className="ours yes">决策、spec、依赖，都是卡片</td><td className="no">一大墙 Markdown</td></tr>
            <tr><td>下周一个决策变了</td><td className="ours yes">依赖自动标 stale</td><td className="no">从头重生成，丢光你的修改</td></tr>
            <tr><td>现在哪一段错了</td><td className="ours yes">标 stale 的卡片会告诉你</td><td className="no">整篇重读一遍</td></tr>
            <tr><td>AI 能回头读吗</td><td className="ours yes">经 MCP 原生读 + 写</td><td className="partial">只能读你贴进去的文本</td></tr>
            <tr><td>六份 PRD 共用一个决策</td><td className="ours yes">一张卡片，六处引用</td><td className="no">六份副本，各自漂走</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">为什么生成出来的 PRD 会<em>漂移。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">生成只是漫长一生的第一天</h3>
            <p>周一生成的那份 PRD，是轻松的那 10%。难的那 90% 在后面几周——范围挪了、某个约束松了、某个指标改了名。生成工具从来没陪你走过那段路；它扔给你一份文档，就走了。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">一份散文文档不知道什么变了</h3>
            <p>你改了定价决策，三条假设了旧定价的需求现在全错了。但一个 Markdown 文件根本不知道那几句话是连在一起的——于是它们继续被当真，直到评审时有人一脚踩上去。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">重新生成会扔掉你的思考</h3>
            <p>PRD 漂得够远时，生成工具唯一的答案就是「再生成一遍」——而这会抹掉你手动做过的每一处修改、每一条批注、每一个来之不易的决策。你不是在维护一份文档，你是在重印它，然后祈祷。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">从<em>生成</em>一份 PRD，到<em>维护</em>一份 PRD。</h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">落成卡片，而不是一墙文字</h3>
            <p>Draftlize 一样用 AI 帮你起草 PRD——但它落成的是结构化卡片：每个决策、每条需求、每个待定问题都是一个可寻址的单元，理由和依赖都挂在上面。同样的初稿，换成一个能被维护、而不只是被重印的形态。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">改一个决策，依赖自动标 stale</h3>
            <p>每张卡片都声明了它依赖什么。改掉定价决策，每条建立在它之上的需求自动变 stale——就像构建系统让一个改动文件的所有下游失效一样。漂移不再是悄悄发生的。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">agent 永远读到最新版</h3>
            <p>Claude Code 或 Cursor 经 MCP 读你的 PRD——读的是活的卡片，不是上周贴进去的旧文本。它也会写回来，于是下一个决策也落成一张卡片，供 PRD 的其余部分依赖。文档和 agent 从结构上就保持同步。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          别的 AI PRD 工具帮你把它写出来；Draftlize 让它写出来之后不烂掉。
        </blockquote>
        <cite>生成初稿是轻松的部分——让产品一直对得上它，才是真正的活。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常见问题</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: '什么是 AI PRD？', a: 'AI PRD 是用 AI 模型从一句 prompt 或几条笔记生成的产品需求文档，而不是从空白页手写。大多数 AI PRD 工具几秒就能出一份文档。难的是之后那几周：产品一往前走、某个决策一变，生成出来的文档就开始过时，而且没人告诉你是哪一部分。' },
            { q: '最好的 AI PRD 生成器是哪个？', a: '取决于你第一版之后要什么。只要一次性草稿，大多数 AI 写作工具都能出。若要 PRD 在决策变化后仍然正确，你需要的是把需求存成彼此相连的单元、而不是一整块散文——Draftlize 把它落成可寻址的卡片，一个决策一变，每条依赖它的需求都会自动标成 stale。' },
            { q: 'ChatGPT 能写 PRD 吗？', a: '能——给个好 prompt，ChatGPT 或 Claude 都能起一份像样的 PRD。局限不在写作，而在记忆：聊天没有持久、可寻址的状态，下一次对话并不知道上一次定了什么，而它产出的文档，从你贴进文档那一刻起就开始漂移。' },
            { q: 'Draftlize 跟 AI PRD 生成器有什么不同？', a: '生成器把成品文档丢给你就走了。Draftlize 是为第一版之后那条长尾造的：PRD 里的每个决策都成为 AI agent 经 MCP 能读也能写的卡片，一处一改，所有依赖它的就自动标成 stale，agent 读到的永远是那个还成立的版本。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">用 AI 写你的 PRD——<em>然后让它一直成立。</em></h2>
        </div>
        <div className="trust-strip">
          <span>注册送 $5</span>
          <span>按量付费</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            从一句 prompt 起草一份 PRD，让它落成卡片，再让 agent 在产品往前走的过程里，把每个决策——以及一切依赖它的东西——一直保持最新。
          </p>
          <a className="btn-primary" href="/">用 $5 免费开始</a>
        </div>
      </section>
      <TemplateCta template="prd" locale="zh" />
    </MarketingShell>
  );
}
